import { getParaInfo } from "@/bussiness/RecordHelper/utils";
import { maxRecordRoleId } from "@/constants/record";
import { IPG, ISpeakerItem } from "@/types/summary";
import { create } from "zustand";

interface ITmpRecordInfo {
  unstableText: string;
  stableText: string;
  lastRecord: IPG | null;
  [key: string]: unknown;
}

interface IParent {
  id: string;
  parent: IParent | null;
}

interface IWordMap {
  [key: string]: {
    parent: IParent;
  };
}

interface IOperatedInfo {
  pi?: string;
  wi?: string;
}

interface RecordState {
  streamName: string;
  meetingName: string;
  currentMic: MediaDeviceInfo | null;
  unstableText: string;
  unstableTime: number;
  stableText: string;
  lastRecord: IPG | null;
  tempRecords: IPG[];
  records: IPG[];
  isOnline: boolean;
  wordMap: IWordMap;
  recentSpeakerList: ISpeakerItem[];
  operatedInfo: IOperatedInfo;
  showSaveDocTip: boolean;
  maxRoleId: number;
  markMap: IMarkMap;
  editedPiArr: string[];
  focusedPara: IPG | null;
  waitingCorrectRecords: IPG[];
}

export interface IMarkMap {
  [key: number]: {
    pi: string;
    pIndex: number;
    markId?: string;
    sId?: string;
  };
}

interface RecordStore extends RecordState {
  setStreamName: (name: string) => void;
  setCurrentMic: (mic: MediaDeviceInfo) => void;
  setMeetingName: (name: string) => void;
  resetStore: () => void;
  setOnline: (isOnline: boolean) => void;
  setTmpRecordInfo: (newTmpRecordInfo: Partial<ITmpRecordInfo>) => void;
  getTmpRecordInfo: () => ITmpRecordInfo;
  setTempRecords: (newTempRecords: IPG[]) => void;
  setRecords: (newRecords: IPG[]) => void;
  getRecords: () => IPG[];
  getWordMap: () => IWordMap;
  getRecentSpeakers: () => ISpeakerItem[];
  unshiftRecentSpeakers: (newSpeakers: ISpeakerItem[]) => void;
  deleteRecentSpeakers: (speakers: string[]) => void;
  updateOperatedInfo: (newOperatedInfo: IOperatedInfo) => void;
  setShowSaveDocTip: (showSaveDocTip: boolean) => void;
  setMaxRoleId: (maxRoleId: number) => void;
  getMaxRoleId: () => number;
  resetRecentSpeakers: () => void;
  setMarkMap: (markMap: IMarkMap) => void;
  addEditedPiArr: (newEditedPi: string) => void;
  setFocusedPara: (focusedPara: IPG | null) => void;
  getWaitingCorrectRecords: () => IPG[];
  setWaitingCorrectRecords: (waitingCorrectRecords: IPG[]) => void;
  getFocusedPara: () => IPG | null;
  getEditedPiArr: () => string[];
}

const recordState: RecordState = {
  streamName: "",
  meetingName: "",
  tempRecords: [],
  records: [],
  currentMic: null,
  lastRecord: {
    role: "0",
    roleName: "",
    speakerName: [],
    sc: [],
    mark: false,
    id: 0,
    pi: "",
  },
  unstableTime: 0,
  unstableText: "",
  stableText: "",
  isOnline: true,
  wordMap: {},
  recentSpeakerList: [],
  operatedInfo: {},
  showSaveDocTip: false,
  maxRoleId: maxRecordRoleId,
  markMap: {},
  editedPiArr: [],
  focusedPara: null,
  waitingCorrectRecords: [],
};

const recordStore = create<RecordStore>((set, get) => ({
  ...recordState,
  setCurrentMic: (mic) => {
    set({ currentMic: mic });
  },
  setStreamName: (name) => {
    set({ streamName: name });
  },
  setMeetingName: (name) => {
    set({ meetingName: name });
  },
  resetStore: () => {
    set({ ...recordState });
  },
  setOnline: (isOnline) => {
    set({ isOnline });
  },
  setShowSaveDocTip: (showSaveDocTip: boolean) => {
    set({ showSaveDocTip });
  },
  setTmpRecordInfo: (newTmpRecordInfo: Partial<ITmpRecordInfo>) => {
    Object.keys(newTmpRecordInfo).forEach((key) => {
      set({
        [key]: newTmpRecordInfo[key],
      });
    });
  },
  getTmpRecordInfo: () => {
    const { lastRecord, unstableText, stableText } = get();
    return { lastRecord, unstableText, stableText };
  },
  setTempRecords: (newTempRecords: IPG[]) => {
    set({ tempRecords: newTempRecords });
  },
  setRecords: (newRecords: IPG[]) => {
    // console.log(
    //   "handle setRecords",
    //   newRecords
    // );
    // 先更新wordMap, 防止record数据更新了，但是wordMap没更新
    const map: IWordMap = {};
    newRecords.forEach((pItem) => {
      pItem.sc.forEach((sItem) => {
        sItem.words.forEach((wItem) => {
          map[wItem.wi] = {
            parent: {
              id: sItem.si,
              parent: {
                id: pItem.pi,
                parent: null,
              },
            },
          };
        });
      });
    });
    set({ wordMap: map });

    // 根据词维护段落的bt,et
    const formatNewRecords = newRecords.map((item) => {
      const paraInfo = getParaInfo(item);
      const { bt, et } = paraInfo;
      item.bt = bt;
      item.et = et;
      return item;
    });
    set({ records: formatNewRecords });
    // 更新unstable time, 当最新段落没有稳态时显示unstable time
    if (!newRecords.length) {
      set({ unstableTime: 0 });
    } else {
      // 修复浏览器兼容性：使用传统方式替代 .at() 方法
      const lastRecord = newRecords[newRecords.length - 1];
      const lastSentence = lastRecord?.sc?.[lastRecord.sc.length - 1];
      const { words: [lastWor] } = lastSentence ?? { words: [] };
      set({ unstableTime: lastWor?.et });
    }
    // console.log("setRecords, map", map);
  },
  getRecords: () => {
    const { records } = get();
    return records;
  },
  getWordMap: () => {
    const { wordMap } = get();
    return wordMap;
  },
  getRecentSpeakers: () => {
    const { recentSpeakerList } = get();
    return recentSpeakerList;
  },
  unshiftRecentSpeakers: (newSpeakers: ISpeakerItem[]) => {
    const { recentSpeakerList } = get();
    newSpeakers.forEach((newSpeaker) => {
      if (!newSpeaker.speakerName || !newSpeaker.speakerName.length) return;
      if (/^发言人\d*$/.test(newSpeaker.speakerName[0])) return;
      const recentSpeakerNameList = recentSpeakerList.map(
        (item) => item.speakerName[0]
      );
      // 先判断是否存在于当前列表中, 存在则移动到最前面
      if (recentSpeakerNameList.includes(newSpeaker.speakerName[0])) {
        const [element] = recentSpeakerList.splice(
          recentSpeakerNameList.indexOf(newSpeaker.speakerName[0]),
          1
        );
        recentSpeakerList.splice(0, 0, element);
      }
      // 否则直接插入到最前面
      else {
        recentSpeakerList.unshift(newSpeaker);
      }
    });
    set({ recentSpeakerList: [...recentSpeakerList] });
  },
  deleteRecentSpeakers: (speakers: string[]) => {
    const { recentSpeakerList } = get();
    const newRecentSpeakers = recentSpeakerList.filter(
      (speaker) => !speakers.includes(speaker.speakerName[0])
    );
    set({ recentSpeakerList: newRecentSpeakers });
  },
  updateOperatedInfo: (newOperatedInfo: IOperatedInfo) => {
    console.log("updateOperatedInfo", newOperatedInfo);
    set({ operatedInfo: newOperatedInfo });
  },
  resetRecentSpeakers: () => {
    set({ recentSpeakerList: [] });
  },
  setMaxRoleId: (maxRoleId: number) => {
    set({ maxRoleId });
  },
  getMaxRoleId: () => {
    const { maxRoleId } = get();
    return maxRoleId;
  },
  setMarkMap: (markMap: IMarkMap) => {
    set({ markMap });
  },
  addEditedPiArr: (newEditedPi: string) => {
    const { editedPiArr } = get();
    console.log("addEditedPiArr", newEditedPi);
    set({ editedPiArr: [...editedPiArr, newEditedPi] });
  },
  getFocusedPara: () => {
    const { focusedPara } = get();
    return focusedPara;
  },
  getEditedPiArr: () => {
    const { editedPiArr } = get();
    return editedPiArr;
  },
  setFocusedPara: (focusedPara) => {
    console.log("focusedPara", focusedPara);
    set({ focusedPara });
  },
  getWaitingCorrectRecords: () => {
    const { waitingCorrectRecords } = get();
    return waitingCorrectRecords;
  },
  setWaitingCorrectRecords: (waitingCorrectRecords) => {
    set({ waitingCorrectRecords });
  },
}));

export default recordStore;
